"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VirtualizedSelectMenu = exports.getMedianIndex = exports.defaultVisibleItemsRange = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var react_window_1 = require("react-window");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var range_1 = tslib_1.__importDefault(require("lodash/range"));
var SelectOption_1 = require("./SelectOption");
var SelectHeading_1 = require("./SelectHeading");
var SelectSeparator_1 = require("./SelectSeparator");
var utils_1 = require("./utils");
exports.defaultVisibleItemsRange = 3;
// eslint-disable-next-line @typescript-eslint/ban-types
var optionGetter = function (_a) {
    var items = _a.items, selectedItem = _a.selectedItem, highlightedIndex = _a.highlightedIndex, getItemProps = _a.getItemProps, optionClassName = _a.optionClassName;
    return function WrappedSelectOption(_a) {
        var index = _a.index, style = _a.style;
        var selectableOptions = utils_1.getSelectableItems(items);
        var item = items[index];
        if (item.type === "option") {
            return (react_1.default.createElement(SelectOption_1.SelectOption, tslib_1.__assign({}, getItemProps({
                key: item.type + "-" + item.value,
                item: item,
                index: selectableOptions.indexOf(item),
                isSelected: selectedItem && item ? selectedItem.value === item.value : false,
                className: optionClassName,
            }), { isFocused: selectableOptions[highlightedIndex] && item
                    ? selectableOptions[highlightedIndex].value === item.value
                    : false, style: style }), item.label));
        }
        else if (item.type === "heading" || item.type === "error") {
            // for now errors look the same as headings
            return (react_1.default.createElement(SelectHeading_1.SelectHeading, { key: item.type + "-" + item.label, style: style }, item.label));
        }
        else if (item.type === "separator") {
            return react_1.default.createElement(SelectSeparator_1.SelectSeparator, { key: item.type + "-" + index, style: style });
        }
        return null;
    };
};
var itemHeightByTypeMap = {
    option: 32,
    heading: 22,
    error: 22,
    separator: 1,
};
var getItemHeight = function (items) {
    return function (index) {
        var itemType = items[index].type;
        return itemHeightByTypeMap[itemType];
    };
};
exports.getMedianIndex = function (array) { return Math.floor(array.length / 2); };
var VirtualizedSelectMenu = /** @class */ (function (_super) {
    tslib_1.__extends(VirtualizedSelectMenu, _super);
    function VirtualizedSelectMenu() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.listRef = react_1.default.createRef();
        _this.scrollToIndex = function (index) {
            if (index === void 0) { index = _this.props.highlightedIndex; }
            if (_this.listRef.current) {
                var items = _this.props.items;
                var selectableOptions = utils_1.getSelectableItems(items);
                var optionIndex = index !== null ? index : exports.getMedianIndex(utils_1.getSelectableItems(items));
                var highlightedOption = selectableOptions[optionIndex];
                // highlightedIndex ignores non selectable items, but scrollToItem doesn't.
                var actualItemIndex = items.indexOf(highlightedOption);
                _this.listRef.current.scrollToItem(actualItemIndex, "center");
            }
        };
        _this.scrollToTop = function () {
            if (!_this.listRef.current) {
                return;
            }
            _this.listRef.current.scrollTo(0);
        };
        _this.componentDidUpdate = function (lastProps) {
            var _a = _this.props, highlightedIndex = _a.highlightedIndex, items = _a.items, setHighlightedIndex = _a.setHighlightedIndex, inputValue = _a.inputValue;
            var lastSelectibleLastItemIndex = utils_1.getSelectableItems(lastProps.items).length - 1;
            var selectiblelastItemIndex = utils_1.getSelectableItems(items).length - 1;
            var lastHighlightIsAtStart = lastProps.highlightedIndex === 0;
            var lastHighlightIsAtEnd = lastProps.highlightedIndex === lastSelectibleLastItemIndex;
            var highlightIsAtStart = highlightedIndex === 0;
            var highlightIsAtEnd = highlightedIndex === selectiblelastItemIndex;
            var isHighlightLoopedBack = lastHighlightIsAtStart && highlightIsAtEnd;
            var isHighlightLoopedForward = lastHighlightIsAtEnd && highlightIsAtStart;
            var isInputValueEmpty = inputValue.trim() === "";
            var isInputValueReset = lastProps.inputValue.trim() !== "" && isInputValueEmpty;
            var isInputValueChanged = lastProps.inputValue.trim() !== inputValue.trim();
            var medianIndex = exports.getMedianIndex(utils_1.getSelectableItems(items));
            if (isInputValueReset) {
                // We need to set highlight to medianIndex manually after clearing inputValue
                setHighlightedIndex(medianIndex);
            }
            else if (isInputValueChanged) {
                // We need to set highlight to 0 manually after filtering
                setHighlightedIndex(0);
            }
            var hasOnlyOneItem = _this.props.items.length === 1;
            if (isInputValueReset) {
                // We need to restore explicitly medianIndex scroll position immediately after inputValue reset
                // even though setHighlightedIndex(medianIndex) is called, because it takes effect after one tick
                _this.scrollToIndex(medianIndex);
            }
            else if (isHighlightLoopedBack || isHighlightLoopedForward) {
                _this.scrollToIndex();
            }
            else if (hasOnlyOneItem) {
                // if there is only one item, we need to explicitly scroll to top
                // in order to handle error messages being scrolled out of view
                _this.scrollToTop();
            }
        };
        return _this;
    }
    VirtualizedSelectMenu.prototype.render = function () {
        var _a = this.props, items = _a.items, selectedItem = _a.selectedItem, highlightedIndex = _a.highlightedIndex, getItemProps = _a.getItemProps, getMenuProps = _a.getMenuProps, className = _a.className, optionClassName = _a.optionClassName, visibleItemsRange = _a.visibleItemsRange;
        var Option = optionGetter({
            items: items,
            selectedItem: selectedItem,
            highlightedIndex: highlightedIndex,
            getItemProps: getItemProps,
            optionClassName: optionClassName,
        });
        var middleItemIndex = exports.getMedianIndex(utils_1.getSelectableItems(items));
        var visibleIndexes = range_1.default(Math.max(middleItemIndex - visibleItemsRange, 0), Math.min(middleItemIndex + visibleItemsRange + 1, items.length));
        var listHeight = visibleIndexes.reduce(function (totalHeight, itemIndex) { return totalHeight + getItemHeight(items)(itemIndex); }, 0);
        return (react_1.default.createElement("div", tslib_1.__assign({}, getMenuProps({ className: classnames_1.default("gd-select-menu-wrapper", className) })),
            react_1.default.createElement("div", { className: "gd-select-menu s-select-menu" },
                react_1.default.createElement(react_window_1.VariableSizeList, { className: "List", ref: this.listRef, itemCount: items.length, itemSize: getItemHeight(items), height: listHeight, width: "100%", overscanCount: 10, estimatedItemSize: itemHeightByTypeMap.option, 
                    // IE shows an unnecessary scrollbar when the list has only one item
                    // this means we have to explicitly disallow that
                    // we also cannot use className prop because react-window sets overflow using style
                    style: items.length === 1 ? { overflow: "hidden" } : undefined }, Option))));
    };
    VirtualizedSelectMenu.prototype.componentDidMount = function () {
        if (!this.props.inputValue) {
            var medianIndex = exports.getMedianIndex(utils_1.getSelectableItems(this.props.items));
            this.props.setHighlightedIndex(medianIndex);
            this.scrollToIndex(medianIndex);
        }
        else {
            this.scrollToIndex();
        }
    };
    // static cannot have <V>
    VirtualizedSelectMenu.defaultProps = {
        selectedItem: null,
        visibleItemsRange: exports.defaultVisibleItemsRange,
    };
    return VirtualizedSelectMenu;
}(react_1.default.Component));
exports.VirtualizedSelectMenu = VirtualizedSelectMenu;
//# sourceMappingURL=VirtualizedSelectMenu.js.map